// Avisos de un caso (D37): lo que hace latir su pestaña en "Abiertos ahora" y lo ordena en Mi día. Sale de quien
// escribio ultimo (ultimoCliente / ultimoEquipo) y de la fecha de seguimiento. Funciones puras, con pruebas.
import { type Caso, esperandoRespuesta, SLA_MINUTOS, textoEspera } from '../api/casos';

// respondio: el cliente escribio despues de nuestra ultima respuesta (aun dentro del plazo).
// espera: el cliente espera respuesta hace mas del plazo (o la primera respuesta se vencio).
// seguimiento: esperabamos al cliente y hoy toca volver a escribirle.
export type Senal = 'espera' | 'respondio' | 'seguimiento';

export const ORDEN_SENAL: Record<Senal, number> = { espera: 0, respondio: 1, seguimiento: 2 };

type DatosSenal = Pick<Caso, 'estado' | 'ultimoCliente' | 'ultimoEquipo' | 'seguimientoEn' | 'primeraRespuesta' | 'origen' | 'creado'>;

// El ultimo en escribir fue el cliente (fechas comparadas como fechas, no como texto).
const clienteUltimo = (c: Pick<Caso, 'ultimoCliente' | 'ultimoEquipo'>) => !!c.ultimoCliente && (!c.ultimoEquipo || Date.parse(c.ultimoCliente) > Date.parse(c.ultimoEquipo));

const finDeHoy = (ahora: number) => {
  const d = new Date(ahora);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

export function senalDe(c: DatosSenal, ahora = Date.now()): Senal | null {
  if (c.estado === 'RESUELTO' || c.estado === 'CERRADO') return null;
  if (c.estado === 'PENDIENTE') return c.seguimientoEn && Date.parse(c.seguimientoEn) <= finDeHoy(ahora) ? 'seguimiento' : null;
  if (clienteUltimo(c)) {
    const min = (ahora - Date.parse(c.ultimoCliente!)) / 60_000;
    if (min >= SLA_MINUTOS) return 'espera';
    // Si nunca le respondimos es un caso nuevo, no "te respondio".
    return c.ultimoEquipo ? 'respondio' : null;
  }
  const primera = esperandoRespuesta(c, ahora);
  return primera != null && primera >= SLA_MINUTOS ? 'espera' : null;
}

export function textoSenal(c: DatosSenal, s: Senal | null, ahora = Date.now()) {
  if (s === 'respondio') return 'Te respondió';
  if (s === 'seguimiento') return c.seguimientoEn && Date.parse(c.seguimientoEn) < new Date(ahora).setHours(0, 0, 0, 0) ? 'Seguimiento vencido' : 'Seguimiento hoy';
  if (s === 'espera') {
    const desde = clienteUltimo(c) ? c.ultimoCliente! : c.creado;
    return `Espera respuesta hace ${textoEspera(Math.max(0, Math.round((ahora - Date.parse(desde)) / 60_000)))}`;
  }
  return '';
}

// Filtros de la barra (en la URL): los mismos se aplican en el servidor (filtroDeVista) y aqui, en Mi dia.
export type EstadoFiltro = '' | 'nuevos' | 'abiertos' | 'pendientes' | 'resueltos';
export type QuienFiltro = '' | 'mios' | 'sintomar';
export type FiltrosCasos = { q: string; grupo: string; estado: EstadoFiltro; quien: QuienFiltro };

export const ESTADOS_FILTRO: Record<Exclude<EstadoFiltro, ''>, Caso['estado'][]> = {
  nuevos: ['NUEVO'],
  abiertos: ['ABIERTO'],
  pendientes: ['PENDIENTE'],
  resueltos: ['RESUELTO', 'CERRADO'],
};

// `encontrados`: lo que encontro el buscador para f.q (D45); sin el, se busca en lo que trae el caso cargado.
export function pasaFiltros(c: Caso, f: FiltrosCasos, miembroId: string | undefined, encontrados?: Set<string>) {
  if (f.grupo && c.grupo !== f.grupo) return false;
  if (f.estado && !ESTADOS_FILTRO[f.estado].includes(c.estado)) return false;
  if (f.quien === 'mios' && (!miembroId || c.asignadoId !== miembroId)) return false;
  if (f.quien === 'sintomar' && c.asignadoId) return false;
  const q = f.q.trim().toLowerCase();
  if (q) {
    const n = q.replace(/^#/, '');
    if (encontrados) return encontrados.has(c.id) || String(c.numero ?? '') === n;
    const texto = [c.asunto, c.correoCliente, c.persona?.nombre, c.empresa?.nombre, c.resumenIA].join(' ').toLowerCase();
    if (!texto.includes(q) && String(c.numero ?? '') !== n) return false;
  }
  return true;
}

// Mi dia: lo que te escribieron, lo que toca perseguir y lo nuevo, separado por tus grupos y los demas.
export function miDia(casos: Caso[], miembroId: string | undefined, misGrupos: string[], ahora = Date.now()) {
  const mio = (c: Caso) => !!miembroId && c.asignadoId === miembroId;
  const deMisGrupos = (c: Caso) => misGrupos.includes(c.grupo);
  const conSenal = casos.map((c) => ({ c, s: senalDe(c, ahora) }));
  const orden = (a: { c: Caso; s: Senal | null }, b: { c: Caso; s: Senal | null }) =>
    (a.s ? ORDEN_SENAL[a.s] : 9) - (b.s ? ORDEN_SENAL[b.s] : 9) || (b.c.ultimoMensaje ?? b.c.creado).localeCompare(a.c.ultimoMensaje ?? a.c.creado);
  const solo = (xs: typeof conSenal) => xs.sort(orden).map((x) => x.c);
  return {
    // Los mios con respuesta del cliente, y los de mis grupos que nadie ha tomado y esperan.
    respondieron: solo(conSenal.filter(({ c, s }) => c.estado !== 'NUEVO' && (s === 'respondio' || s === 'espera') && (mio(c) || (!c.asignadoId && deMisGrupos(c))))),
    seguimientos: solo(conSenal.filter(({ c, s }) => s === 'seguimiento' && (mio(c) || (!c.asignadoId && deMisGrupos(c))))),
    nuevosMios: solo(conSenal.filter(({ c }) => c.estado === 'NUEVO' && deMisGrupos(c))),
    nuevosOtros: solo(conSenal.filter(({ c }) => c.estado === 'NUEVO' && !deMisGrupos(c))),
    // Casos de otros grupos que esperan respuesta hace mas del plazo (para avisar que alli hace falta ayuda).
    esperaOtros: conSenal.filter(({ c, s }) => s === 'espera' && !deMisGrupos(c)).length,
  };
}

// El siguiente caso que necesita algo (para "Siguiente" en el caso abierto): primero los que esperan respuesta.
export function siguienteCaso(casos: Caso[], actualId: string, miembroId: string | undefined, misGrupos: string[], ahora = Date.now()) {
  const d = miDia(casos, miembroId, misGrupos, ahora);
  return [...d.respondieron, ...d.seguimientos, ...d.nuevosMios].find((c) => c.id !== actualId) ?? null;
}

// Casos a los que el cliente acaba de escribir (D40): su ultimoCliente cambio desde la lectura anterior y el caso es
// tuyo, o esta sin tomar en uno de tus grupos. Para la notificacion del navegador y el contador de la pestaña.
export function respuestasNuevas(antes: Map<string, string | null>, casos: Caso[], miembroId: string | null, misGrupos: string[]) {
  return casos.filter((c) => {
    if (!c.ultimoCliente || !antes.has(c.id)) return false;
    const previo = antes.get(c.id);
    if (previo && Date.parse(previo) >= Date.parse(c.ultimoCliente)) return false;
    if (c.ultimoEquipo && Date.parse(c.ultimoEquipo) >= Date.parse(c.ultimoCliente)) return false;
    return c.asignadoId ? c.asignadoId === miembroId : misGrupos.includes(c.grupo);
  });
}
